/**
 * Exportação de listas: CSV, planilha e PDF por impressão.
 *
 * Porte de `ui_kits/vecchia_app/exportUtils.jsx`. Nenhuma biblioteca externa —
 * o "PDF" é a janela de impressão do navegador com a folha de estilo do
 * produto, que é o que a especificação pede e não custa 300 KB de bundle.
 */

export type FormatoDeExportacao = 'csv' | 'xlsx' | 'pdf';

export interface ColunaExportavel<Linha> {
    header: string;
    value: (linha: Linha) => string | number | null | undefined;
}

export interface CargaExportavel<Linha> {
    title: string;
    subtitle?: string;
    columns: ColunaExportavel<Linha>[];
    rows: Linha[];
}

export function baixarArquivo(nome: string, tipo: string, conteudo: string): void {
    const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function texto(v: unknown): string {
    return v == null ? '' : String(v);
}

function escaparHtml(v: unknown): string {
    return texto(v).replace(/&/g, '&amp;').replace(/</g, '&lt;');
}

export function paraCsv<Linha>(colunas: ColunaExportavel<Linha>[], linhas: Linha[]): string {
    const campo = (v: unknown) => `"${texto(v).replace(/"/g, '""')}"`;
    const cabecalho = colunas.map((c) => campo(c.header)).join(';');
    const corpo = linhas.map((l) => colunas.map((c) => campo(c.value(l))).join(';'));
    /* BOM na frente: sem ele o Excel em pt-BR abre o arquivo em Latin-1 e todo
       acento vira caractere trocado. */
    return `\ufeff${[cabecalho, ...corpo].join('\r\n')}`;
}

export function paraTabelaXls<Linha>(colunas: ColunaExportavel<Linha>[], linhas: Linha[]): string {
    return `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body>`
        + `<table border="1"><thead><tr>${colunas.map((c) => `<th>${escaparHtml(c.header)}</th>`).join('')}</tr></thead><tbody>`
        + linhas.map((l) => `<tr>${colunas.map((c) => `<td>${escaparHtml(c.value(l))}</td>`).join('')}</tr>`).join('')
        + '</tbody></table></body></html>';
}

/** Linhas por exportação: "todos os registros do filtro", sem cortar na página. */
export const TODOS_OS_REGISTROS = 100_000;

/**
 * A janela de impressão, aberta AGORA, dentro do clique.
 *
 * Aberta depois de uma espera — buscar todos os registros, por exemplo —, o
 * navegador a bloqueia como pop-up; e isso nunca aparece no jsdom.
 */
export function abrirJanelaDeImpressao(): Window | null {
    return window.open('', '_blank', 'width=980,height=720');
}

/**
 * As folhas de estilo desta página, com endereço absoluto.
 *
 * O PDF carregava `/styles.css`, que não existe no bundle (o nginx devolvia o
 * `index.html`), e saía sem token nem fonte. A janela nova também não resolve
 * caminho relativo: o endereço vai inteiro.
 */
function estilosDaPagina(): string {
    return Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
        .map((el) => (el instanceof HTMLLinkElement
            ? `<link rel="stylesheet" href="${escaparHtml(new URL(el.getAttribute('href') ?? '', location.href).href)}">`
            : `<style>${el.textContent ?? ''}</style>`))
        .join('');
}

function imprimirPdf<Linha>({ title, subtitle = '', columns, rows }: CargaExportavel<Linha>, janela?: Window | null): boolean {
    const w = janela === undefined ? abrirJanelaDeImpressao() : janela;
    if (!w) return false;

    w.document.write(
        `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escaparHtml(title)}</title>`
        + estilosDaPagina()
        + '<style>@page{size:A4 landscape;margin:14mm}body{background:#fff;padding:0;font-family:var(--font-ui)}'
        + 'h1{font-family:var(--font-display);font-size:20px;margin:0 0 4px;letter-spacing:-.02em}'
        + 'p.sub{margin:0 0 18px;font-size:12px;color:var(--text-secondary)}'
        + 'table{width:100%;border-collapse:collapse;font-size:11px}'
        + 'th{text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:var(--text-secondary);border-bottom:1px solid var(--border-strong);padding:7px 8px}'
        + 'td{padding:7px 8px;border-bottom:1px solid var(--border-subtle);color:var(--text-primary)}'
        + 'footer{margin-top:18px;font-size:10px;color:var(--text-tertiary)}</style></head><body>'
        + `<h1>${escaparHtml(title)}</h1><p class="sub">${escaparHtml(subtitle)}</p>`
        + `<table><thead><tr>${columns.map((c) => `<th>${escaparHtml(c.header)}</th>`).join('')}</tr></thead><tbody>`
        + rows.map((l) => `<tr>${columns.map((c) => `<td>${escaparHtml(c.value(l))}</td>`).join('')}</tr>`).join('')
        + `</tbody></table><footer>Fórmula MED · gerado em ${new Date().toLocaleString('pt-BR')}</footer>`
        + '</body></html>',
    );
    w.document.close();
    window.setTimeout(() => { w.focus(); w.print(); }, 400);
    return true;
}

function apelido(titulo: string): string {
    return titulo
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

/** Executa a exportação e devolve a mensagem de retorno para o aviso. */
export function exportarComo<Linha>(formato: FormatoDeExportacao, carga: CargaExportavel<Linha>, janela?: Window | null): string {
    const nome = apelido(carga.title);
    if (formato === 'csv') {
        baixarArquivo(`${nome}.csv`, 'text/csv;charset=utf-8', paraCsv(carga.columns, carga.rows));
        return 'CSV baixado';
    }
    if (formato === 'xlsx') {
        baixarArquivo(`${nome}.xls`, 'application/vnd.ms-excel', paraTabelaXls(carga.columns, carga.rows));
        return 'Planilha baixada';
    }
    /* Pop-up bloqueado é o caso comum, e o silêncio faria parecer defeito. */
    return imprimirPdf(carga, janela) ? 'Abrindo a janela de impressão' : 'Permita pop-ups para gerar o PDF';
}
